import type { SourceTableDto } from '@/api/control/missionImpactAPI.schemas';

/** A single option in the gating-parent picker. */
export interface ParentTableOption {
  /** Source table id submitted as the parent. */
  id: string;
  /** Text shown in the picker, e.g. "Case (incident)". */
  label: string;
}

/** The table's trimmed display name, or undefined when it has none. */
function displayNameOf(table: SourceTableDto): string | undefined {
  const displayName = table.displayName?.trim();
  return displayName && displayName.length > 0 ? displayName : undefined;
}

/**
 * Formats a table the same way the initial table picker does: the display name followed by the
 * logical name in brackets (e.g. "Case (incident)"). The logical name alone is shown when there is
 * no display name, or when it matches the display name.
 */
export function formatParentTableLabel(table: SourceTableDto): string {
  const displayName = displayNameOf(table);
  if (!displayName || displayName === table.logicalName) {
    return table.logicalName;
  }
  return `${displayName} (${table.logicalName})`;
}

/**
 * Builds the gating-parent picker options for a table: every other table in the source (a table
 * cannot be its own parent), sorted by display name ascending (falling back to the logical name),
 * case-insensitively, with the logical name as a tie-breaker.
 */
export function buildParentTableOptions(
  table: SourceTableDto,
  candidates: SourceTableDto[],
): ParentTableOption[] {
  return candidates
    .filter((candidate) => candidate.id !== table.id)
    .map((candidate) => ({
      candidate,
      sortKey: displayNameOf(candidate) ?? candidate.logicalName,
    }))
    .sort(
      (a, b) =>
        a.sortKey.localeCompare(b.sortKey, undefined, { sensitivity: 'base' }) ||
        a.candidate.logicalName.localeCompare(b.candidate.logicalName, undefined, {
          sensitivity: 'base',
        }),
    )
    .map(({ candidate }) => ({ id: candidate.id, label: formatParentTableLabel(candidate) }));
}
